import OpenPanelLeft16 from '@carbon/icons-react/es/open-panel--left/16';
import { useBoolean } from 'ahooks';
import React, { FC, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { PermissionSetType, ProjectId } from 'api/models';
import { FieldWithValue } from 'api/tasks';
import { LinkButton } from 'components/atoms/button';
import { Loading } from 'components/atoms/loading';
import { toastService } from 'components/molecules/toast';
import { HelperText01, Text02 } from 'components/styles';
import { STATUS_FIELD_NAME } from 'data/fields/hooks';
import { useProjectDetail } from 'data/projects/hooks';
import {
  useCurrentUserProjectAccess,
  useUserPermissionSetType,
} from 'hooks/permissions';
import { useTranslation } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { useUpdateProjectMutation } from 'queries/project';
import { byFieldName } from 'utils/by-field-name';
import { useProjectCustomFields } from 'utils/project-fields/use-project-fields';
import { ResponsiveCard } from '../../style';
import { ProjectDefaultFields } from './project-default-fields';
import { ProjectOverviewExpanded } from './project-overview-expanded';
import { ComputeDefaultProjectFields } from './utils';

const Card = styled(ResponsiveCard)`
  min-height: 310px;
  max-height: 310px;
`;
const Header = styled.div`
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 54px;
    z-index: 0;
    background: var(--private-01);
  }

  > * {
    position: relative;
    z-index: 1;
  }
`;
const SubTitle = styled.p`
  ${HelperText01};
  ${Text02};
`;
const LoadingWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 130px;
`;

const StyledLinkButton = styled(LinkButton)`
  padding: var(--spacing-03);
`;

export const ProjectOverviewProjectDetails: FC<{ projectId: number }> = ({
  projectId,
}) => {
  const [
    isProjectOverviewExpanded,
    {
      setTrue: openProjectOverviewExpanded,
      setFalse: closeProjectOverviewExpanded,
    },
  ] = useBoolean(false);
  const { data: projectFields, isLoading: projectFieldsLoading } =
    useProjectCustomFields();
  const { data: projectData, isLoading: projectDataLoading } = useProjectDetail(
    ProjectId(projectId),
  );
  const customFields = useMemo(() => {
    return projectFields.filter((field) => {
      return !byFieldName(field, STATUS_FIELD_NAME) && field.isEnabled;
    });
  }, [projectFields]);
  const defaultFields = useMemo(() => {
    return ComputeDefaultProjectFields(projectFields);
  }, [projectFields]);

  const { isProjectAdmin } = useCurrentUserProjectAccess(ProjectId(projectId));
  const currentUserPermissionSetType = useUserPermissionSetType();
  const isAccountAdmin =
    currentUserPermissionSetType === PermissionSetType.AccountAdmin;
  const canEdit = isAccountAdmin || isProjectAdmin;
  const isDefaultFieldsReadOnly = !canEdit;

  const { user } = useUser();
  const errorMessage = useTranslation('error');
  const errorTitle = useTranslation('error_saving');
  const { mutate } = useUpdateProjectMutation(ProjectId(projectId));
  const handleOnCustomFieldChange = (
    fieldValue,
    { fieldId }: FieldWithValue,
  ) => {
    mutate(
      { fields: [{ fieldId, fieldValue }] },
      {
        onError: () => {
          toastService({
            title: errorTitle,
            message: errorMessage,
          }).error();
        },
      },
    );
  };

  const handleOnDefaultFieldChange = (payload) => {
    mutate(payload, {
      onError: () => {
        toastService({
          title: errorTitle,
          message: errorMessage,
        }).error();
      },
    });
  };

  return (
    <Card
      title={
        <Header>
          <strong>
            <FormattedMessage id="project.overview.project_details.title" />
          </strong>
          <SubTitle>
            <FormattedMessage
              id="project.overview.project_details.sub_title"
              values={{ companyName: user.company.companyName }}
            />
          </SubTitle>
        </Header>
      }
    >
      {projectDataLoading || projectFieldsLoading ? (
        <LoadingWrapper>
          <Loading />
        </LoadingWrapper>
      ) : (
        projectData && (
          <>
            <ProjectDefaultFields
              projectData={projectData}
              defaultFields={defaultFields}
              isDefaultFieldsReadOnly={isDefaultFieldsReadOnly}
              onCustomFieldChange={handleOnCustomFieldChange}
              onDefaultFieldChange={handleOnDefaultFieldChange}
            />
            {customFields.length ? (
              <>
                <StyledLinkButton
                  onClick={openProjectOverviewExpanded}
                  icon={<OpenPanelLeft16 />}
                  data-cy="project.overview.see-more-fields"
                >
                  <FormattedMessage id="project.overview.see_more_fields" />
                </StyledLinkButton>
                <ProjectOverviewExpanded
                  projectData={projectData}
                  isDefaultFieldsReadOnly={isDefaultFieldsReadOnly}
                  defaultFields={defaultFields}
                  customFields={customFields}
                  visible={isProjectOverviewExpanded}
                  closeDrawer={closeProjectOverviewExpanded}
                  onCustomFieldChange={handleOnCustomFieldChange}
                  onDefaultFieldChange={handleOnDefaultFieldChange}
                />
              </>
            ) : null}
          </>
        )
      )}
    </Card>
  );
};
